import { Config, CONFIG } from "src/app/_models/config";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { ENVIRONMENT, API, API_ERROR_CODES, FLIGHT_STATUS, GATE_CONSTANTS, BAG_STATUS, TRH_UNITED_URL } from 'src/app/_constants/app.constants';
import { CustomerDetails } from "src/app/_models/easySearchApi.model";
import { baggageResponse, ObjectLiteral } from "src/app/_models/user";
import { InsertFlightSegment, DeleteFlightSegment, ChangeFlightSegmentsRequest, FlightOption, FlightOptionsRequest, FlightOptionsResponse, newEmptyFlightOption, InsertFlightSegmentBookingClasses } from "src/app/_models/availableFlights.model";
import { newFlightSegment, FlightDetails, Segment, Traveler } from "src/app/_models/flightSegment.model";
import { Temporal } from '@js-temporal/polyfill';
import { IATA_TIMEZONE } from "src/app/_constants/timezones.constants";
import { AuthService } from '../auth/auth.service';
import { LoggerService } from 'src/app/_services/ui/logger/logger.service';
import { SessionService } from 'src/app/_services/session/session.service';
import { SESSION_STORAGE_SERVICE } from 'src/app/_services/session/session.service';
import { StorageService } from 'ngx-webstorage-service';
import { AppModule } from "src/app/app.module";
import { ServiceModule } from 'src/app/_services/service.module';
import { flightResposne } from "src/app/_models/flight";
import { CryptoService } from "../../crypto/crypto.service";
import { catchError, of } from "rxjs";

@Injectable({
  providedIn: ServiceModule
})
export class FlightService {

  isHelpRequested: boolean = false
  activeTraveler: Traveler = null
  activeSegment: Segment = null
  activeSegments: Segment[] = []
  showBoardingPass = false
  arrivalSegment: Segment
  departureSegment: Segment
  flightOptionsSearchDate: Temporal.Instant
  flightOptionsCurrentDate: Temporal.Instant
  selectedFlightSegments: Segment[]
  hasTrhNavigation = false;

  private _flightDetails: FlightDetails = null
  get flightDetails(): FlightDetails { return this._flightDetails ? { ...this._flightDetails } : null }
  set flightDetails(flightDetails: FlightDetails) { this._flightDetails = flightDetails }

  private _customerDetails: CustomerDetails[] = null
  get customerDetails(): CustomerDetails[] { return this._customerDetails }
  set customerDetails(customerDetails: CustomerDetails[]) { this._customerDetails = customerDetails }

  private _flightOptions: FlightOptionsResponse;
  get flightOptions(): FlightOptionsResponse { return this._flightOptions }
  set flightOptions(flightOptions: FlightOptionsResponse) { this._flightOptions = flightOptions }

  private _selectedFlight: FlightOption
  get selectedFlight(): FlightOption { return this._selectedFlight }
  set selectedFlight(selectedFlight: FlightOption) { this._selectedFlight = selectedFlight }

  constructor(
    @Inject(CONFIG) private config: Config,
    @Inject(SESSION_STORAGE_SERVICE) private storage: StorageService,
    private http: HttpClient,
    private logger: LoggerService,
    private sessionService: SessionService,
    private authService: AuthService,
    private cryptoService: CryptoService,
  ) { }

  /**
   * @name searchFlightDetails
   * @param lastName
   * @param destination
   * @param dob
   * @param retryCount
   * @desc 
   *
   */

  async searchFlightDetails(lastName: string, destination: string, dob: string, retryCount?: number): Promise<void> {
    retryCount = retryCount || 0;
    this.logger.debug('FlightService.searchFlightDetails()', arguments);
    let url = `${ENVIRONMENT.PROPERTIES.apiUrl}${API.FLIGHT}`;
    let timeStamp = Temporal.Now.instant().epochMilliseconds;
    let body = {
      action: 'EASY_SEARCH',
      lastName: lastName,
      destination: destination,
      dob: dob,
      timestamp: timeStamp
    };

    let headers = new HttpHeaders({
      'Hostname': this.authService.getHostName(),
      'Xsrf': this.authService.token
    })

    const flightResponse: any = await new Promise((res: any, rej: any) => {
      this.http.post(encodeURI(url), body, { headers }).pipe(
        catchError(err => {
          return of({ success: false })
        })
      ).subscribe((data: any) => {
        res(data)
      })
    })

    const apiResponse = {
      code: flightResponse.code,
      success: flightResponse.success,
      ...flightResponse.data,
    }

    this.logger.debug('searchFlightDetails() apiResponse :: ', apiResponse);
    let response: any;

    response = API_ERROR_CODES.includes(apiResponse['code'])
      ? apiResponse
      : this.identifyEasySearchFlow(timeStamp, lastName, apiResponse);


    if (response["success"]) {
      if (response['multiplePnr']) {
        this.setCustomerDetails(response['data']);
      } else {
        this.setFlightDetails(response['data']);
      }
      return response;
    } else {
      if (retryCount == maxCalloutRetry) throw response;

      if (response["code"] == 403) {
        try {
          await this.authService.refreshToken();
          this.logger.debug('Flight service : searchFlightDetails() : Token refresh :: Success');
          return await this.searchFlightDetails(lastName, destination, dob, ++retryCount);

        } catch (e) {
          this.logger.debug('Flight service : searchFlightDetails() : Exception thrown :: ', e.message);
          throw e;
        }
      } else throw response;
    }
  }

  /**
   *
   * Parses the response and sets it to the service
   *
   * @param pnr
   * @param lastName
   *
   */
  async getFlightDetails(pnr: string, lastName: string, stationId: string, retryCount: number = 0): Promise<any> {
    this.logger.debug('FlightService: getFlightDetails()', arguments);
    let url = `${ENVIRONMENT.PROPERTIES.apiUrl}${API.FLIGHT}`;
    let timeStamp = Temporal.Now.instant().epochMilliseconds;
    let body = {
      action: 'PNR_SEARCH',
      pnr: pnr,
      LastName: lastName,
      timestamp: timeStamp,
      stationId: stationId,
    }
    let headers = new HttpHeaders({
      'Hostname': this.authService.getHostName(),
      'Xsrf': this.authService.token,
      'Mock': String(this.storage.has('mock'))
    })

    const flightResponse: any = await new Promise((res: any, rej: any) => {
      this.http.post(encodeURI(url), body, { headers }).pipe(
        catchError(err => {
          return of({ success: false })
        })
      ).subscribe((data: any) => {
        res(data)
      })
    })

    const apiResponse = {
      code: flightResponse.code,
      success: flightResponse.success,
      ...flightResponse.data,
    }

    // await this.http.post(encodeURI(url), body, { headers }).toPromise();


    this.logger.debug('getFlightDetails() apiResponse :: ', apiResponse);
    let response: any;

    response = API_ERROR_CODES.includes(apiResponse['code'])
      ? apiResponse
      : this.parsePnr(timeStamp, stationId, lastName, apiResponse);

    if (response["success"]) {
      this.setFlightDetails(response["data"]);
      return response;
    } else {
      if (retryCount == maxCalloutRetry) throw response;

      if (response["code"] == 403) {
        try {
          await this.authService.refreshToken();
          this.logger.debug('Flight service : getFlightDetails() : Token refresh :: Success');
          return await this.getFlightDetails(pnr, lastName, stationId, ++retryCount);

        } catch (e) {
          this.logger.debug('Flight service : getFlightDetails() : Exception thrown :: ', e.message);
          throw e;
        }
      } else throw response;
    }
  }

  async searchFlightOptions(request: FlightOptionsRequest, retryCount: number = 0): Promise<any> {
    this.logger.debug('FlightService.searchFlightOptions(request)', request);
    let url = `${ENVIRONMENT.PROPERTIES.apiUrl}${API.FLIGHT}`;
    let body = {
      action: 'SHOPPING_SERVICE',
      retryCount: retryCount,
      ...request
    }
    let headers = new HttpHeaders({
      'Hostname': this.authService.getHostName(),
      'Xsrf': this.authService.token
    })

    const flightResponse: any = await new Promise((res: any, rej: any) => {
      this.http.post(encodeURI(url), body, { headers }).pipe(
        catchError(err => {
          return of({ success: false })
        })
      ).subscribe((data: any) => {
        res(data)
      })
    })

    const response = {
      code: flightResponse.code,
      success: flightResponse.success,
      ...flightResponse.data,
    }

    // await this.http.post(encodeURI(url), body, { headers }).toPromise();

    if (response?.success) {
      this.flightOptions = this.parseFlightOptions(response);
      return response;
    } else {
      if (retryCount == maxCalloutRetry) throw response;

      if (response["code"] == 403) {
        try {
          await this.authService.refreshToken();
          this.logger.debug('Flight service : searchFlightOptions() : Token refresh :: Success');
          return await this.searchFlightOptions(request, ++retryCount);

        } catch (e) {
          this.logger.debug('Flight service : searchFlightOptions() : Exception thrown :: ', e.message);
          throw e;
        }
      } else throw response;
    }
  }


  /**
  * @name changeFlightSegments
  * @param request
  * @param retryCount
  */
  async changeFlightSegments(request: ChangeFlightSegmentsRequest, retryCount: number = 0): Promise<any> {
    this.logger.debug('FlightService.changeFlightSegments()', arguments);
    let url = `${ENVIRONMENT.PROPERTIES.apiUrl}${API.FLIGHT}`;
    let body = {
      action: 'CHANGE_SEGMENTS',
      ...request
    }

    const flightResponse: any = await new Promise((res: any, rej: any) => {
      this.http.post(encodeURI(url), body, {
        headers: new HttpHeaders({
          'Hostname': this.authService.getHostName(),
          'Xsrf': this.authService.token
        })
      }).pipe(
        catchError(err => {
          return of({ success: false })
        })
      ).subscribe((data: any) => {
        res(data)
      })
    })

    const response = {
      code: flightResponse.code,
      success: flightResponse.success,
      ...flightResponse.data,
    }

    // await this.http.post(encodeURI(url), body, {
    //     headers: new HttpHeaders({
    //       'Hostname': this.authService.getHostName(),
    //       'Xsrf': this.authService.token
    //     })
    //   }).toPromise();

    if (response?.success) {
      return response;
      //this._flightOptions = response["data"];
    } else {
      if (retryCount == maxCalloutRetry) throw response;

      if (response["code"] == 403) {
        try {
          await this.authService.refreshToken();
          this.logger.debug('FlightService.changeFlightSegments() : Token refresh :: Success');
          return await this.changeFlightSegments(request, ++retryCount);

        } catch (e) {
          this.logger.debug('FlightService.changeFlightSegments() : Exception thrown :: ', e.message);
          throw e;
        }
      } else throw response;
    }
  }

  /**
   * 
   * @param timeStamp 
   * @param lastName 
   * @param apiResponse 
   * @returns 
   */
  identifyEasySearchFlow(timeStamp: number, lastName: String, apiResponse: ObjectLiteral) {
    let response: ObjectLiteral;
    if (apiResponse?.['pnrFightCardResponse'] == null && apiResponse?.['customerDetail']) {
      response = { success: true, data: apiResponse?.['customerDetail'], multiplePnr: true };
    } else if (apiResponse?.['customerDetail'] == null && apiResponse?.['pnrFightCardResponse']) {
      response = this.parsePnr(timeStamp, "", lastName, apiResponse['pnrFightCardResponse']);
    } else {
      this.logger.debug('Response format issue, all conditions failed ::::,', response);
      response = { success: false, code: 200, data: apiResponse };
    }
    return response;
  }

  /**
   * 
   * @param phone 
   * @returns 
   */
  async capturePNR(phone: string): Promise<boolean> {
    let passenger: CustomerDetails;
    this.customerDetails.forEach(customer => {
      if (customer && customer.contactPhoneInfo) {
        customer.contactPhoneInfo.forEach(detail => {
          if (detail.phone == phone) {
            passenger = customer;
          }
        })
      }
    })
    this.logger.debug('FlightService :: capturePNR', passenger)
    if (passenger) {
      await this.getFlightDetails(passenger.pnr, passenger.lastName, '');
      this.sessionService.pnrDetails = { pnr_no: passenger.pnr, last_name: passenger.lastName };
      return true;
    }
    return false;
  }

  /**
   *
   * Parses the response and sets it to the service
   *
   * @param flightDetails - missing interface for type
   *
   */
  setFlightDetails(flightDetails): void {
    this.logger.debug('FlightService: setFlightDetails()', this);
    this._flightDetails = flightDetails;
    this._flightDetails.segments = this.mapSegmentsData(this._flightDetails.segments, this._flightDetails.confirmationID);
    this.setActiveSegment(this._flightDetails.segments);
    this.setActiveTraveler(this._flightDetails.travelers.length == 1 ? this._flightDetails.travelers[0] : null);
  }

  setCustomerDetails(customerDetails: CustomerDetails[]) {
    this.logger.debug('FlightService: setCustomerDetails()');
    this._customerDetails = customerDetails;
  }

  /**
   *
   * Sets the activeTraveler
   *
   * @param activeTraveler
   *
   */
  setActiveTraveler(activeTraveler): void {
    if (activeTraveler) {
      this.flightDetails.segments.forEach(segment => {
        segment.seatNumber = '--';
        if (activeTraveler.seats && activeTraveler.seats.length) {
          activeTraveler.seats.some(seat => {
            if (seat.seatNumber && seat.segmentNumber == segment.segmentNumber) {
              segment.seatNumber = seat.seatNumber;
              return true;
            }
            return false
          })
        }
      })
      this.activeTraveler = activeTraveler;
    }
    this.checkIrrops();
    this.logger.debug('FlightService: setActiveTraveler()', this._flightDetails, activeTraveler);
  }

  /**
   * Redirect to TRH for irrops
   */
  checkIrrops() {
    // const trhAvailableStation = ['ord'];
    this.hasTrhNavigation = false;
    this._flightDetails.segments.forEach(segment => {
      if (
        segment.isIrrop &&
        segment.flightStatus.className !== 'on-time' &&
        // trhAvailableStation.includes(segment.departureAirport.iataCode.toLowerCase()) &&
        Temporal.Now.instant().add({ hours: 23, minutes: 30 }).epochMilliseconds > segment.departureDateTimeLong
      ) {
        this.hasTrhNavigation = true;
      }
    })
  }

  redirectToTrh() {
    let url = TRH_UNITED_URL.QA;
    if (ENVIRONMENT.PROPERTIES.name === 'master') {
      url = TRH_UNITED_URL.PRD;
    }
    url += `/${this.flightDetails.confirmationID}/${this.activeTraveler.surname}`;
    const os = window.navigator.userAgent.toLowerCase();
    if (os.includes('android')) {
      window.location.href = url;
      return
    }
    if (os.includes('iphone')) {
      window.location.href = url;
      return
    }
    window.location.href = url;
  }


  /**
   *
   * transforms the segment array and adds travelTime
   *
   * @param segments
   *
   */
  mapSegmentsData(segments: any[], confirmationID: string): any[] {
    const transformedSegments = segments.map((segment, index) => {
      segment.travelTime = this.getTravelTime(segment.travelTime);
      segment.confirmationID = confirmationID;
      segment.isFinalDestination = segments.length - 1 == index;
      return segment;
    });
    return transformedSegments;
  }

  /**
   *
   *  sets the activeSegment
   *
   */
  setActiveSegment(segments: any[]) {
    this.activeSegment = segments.find(segment => segment.activeFlightSegment == true);
  }

  /**
   *  getTravelTime - get the travel time
   *
   *  @param minutes
   */
  getTravelTime = (minutes) => {
    var hours = Math.trunc(minutes / 60)
    minutes = minutes % 60
    // return hours + 'H, ' + minutes + 'M'
    return hours > 0 ? `${hours}H, ${minutes}M` : `${minutes}M`;
  }

  /**
   *
   * Sets the _flightDetails property to null
   *
   */
  clearFlightDetails(): void {
    this.logger.debug('FlightService: clearFlightDetails()');
    this._flightDetails = null;
    this.activeTraveler = null;
    this.activeSegment = null;
    this._customerDetails = null;
  }

  /**
   * Checks if the traveler is checked in
   */
  isCheckedIn() {
    this.logger.debug('FlightService: isCheckedIn()', ENVIRONMENT.PROPERTIES.name);
    return this.sessionService.locationData.is_checked_in == "True";
  }

  /**
   * Returns if we need to route user to self-service page
   * @returns boolean
   */
  isIrropRoute(): boolean {
    this.logger.debug("FlightService: isIrropRoute()", this.flightDetails);
    return (this.flightDetails?.globalirrops == "True" && !this.flightDetails?.isIrrop);
  }

  /**
   * 
   * @param segment 
   * @returns 
   */
  mapFlightDetailsToFlightOptions(segment = this.activeSegment): FlightOption {

    if (!segment) return null;

    let flightOptions: FlightOption = {
      ...newEmptyFlightOption(),
      departDateTime: segment.departureDateTime,
      destinationDateTime: segment.arrivalDateTime,
      flightNumber: segment.flightNumber.toString(),
      flightInfo: {
        scheduledArrivalDateTime: segment.arrivalDateTime,
        actualDepartureDateTime: segment.departureDateTime,
        actualArrivalDateTime: segment.arrivalDateTime,
      },
      connections: [],
      operatingCarrierCode: segment.carrierCode,
      connectTimeMinutes: segment.travelTime || '--',
      operatingCarrierName: segment.carrierName || '--',
      flightStatus: segment.flightStatus,
      origin: segment.departureAirport.iataCode,
      destination: segment.arrivalAirport.iataCode,
    }
    return flightOptions;
  }

  /**
   * 
   * @param selectedFlight 
   * @returns 
   */

  mapFlightOptionsToFlightDetails(selectedFlight: FlightOption = this.selectedFlight): Segment {
    if (!selectedFlight) return null;

    let updatedSegment: Segment = {
      ...newFlightSegment(),
      confirmationID: this.activeSegment.confirmationID, //PNR will remain same
      segmentNumber: this.activeSegment.segmentNumber,
      activeFlightSegment: this.activeSegment.activeFlightSegment,
      flightNumber: parseInt(selectedFlight.flightNumber),
      arrivalAirport: this.activeSegment.arrivalAirport,
      arrivalDateTime: selectedFlight.destinationDateTime,
      arrivalGate: selectedFlight.arrivalGate,
      arrivalTerminal: '--', // Not available
      baggageCarouselId: '--',// Not available
      boardDateTime: null, // Not available
      carrierCode: 'UA', // Not available
      departureAirport: this.activeSegment.departureAirport,
      departureDateTime: selectedFlight.departDateTime,
      departureGate: '--', //Not available,
      departureTerminal: '--', //Not available
      effectiveHub: this.activeSegment.arrivalAirport.iataCode,
      flightStatus: selectedFlight.flightStatus,
      isIrrop: false, //Not available
      legStatus: selectedFlight.flightStatus.description,
      localSchDeptDtm: selectedFlight.departDateTime,
      travelTime: 0 //Not available
    }
    return updatedSegment;
  }

  /**
   * parsePnr - parse the response data object
   * @param data - response object data
   */

  parsePnr(timestamp, stationId, LastName, data) {

    this.logger.debug('FlightService.parsePnr()', this)

    if (data && data.activateTravelPlan && data.activateTravelPlan.travelPlan) {
      const travelers = this.getTravelers(LastName, data)
      const segments = this.getSegments(timestamp, stationId, data)
      const keyAttributes = data.activateTravelPlan.keyAttributes || ''
      const score = data.activateTravelPlan.score || 0
      const docCheckRegion = data.activateTravelPlan.travelPlan.docCheckRegion || ''
      const isInternational = data.activateTravelPlan.travelPlan.isInternational === "true" ? true : false
      const globalirrops = data.activateTravelPlan.globalirrops || ''
      const travelReady = data.activateTravelPlan.isTravelReady || false
      const isIrrop = this.checkisIrrop(segments)

      // reset all available active segments
      this.activeSegments = []

      // populate a list of all available active segments
      segments.forEach((segment) => {
        if (
          Temporal.Now.instant().subtract({ hours: 23, minutes: 30 }).epochMilliseconds < segment.departureDateTimeLong
          && Temporal.Now.instant().add({ hours: 168 }).epochMilliseconds > segment.departureDateTimeLong
        ) {
          this.activeSegments.push(segment)
        }
      })

      // do we have active segments?
      if (this.activeSegments.length > 0) {

        // sort them by departure time
        this.activeSegments.sort((a, b) => a.departureDateTimeLong - b.departureDateTimeLong)

        // remove leading segments that are not cancelled or not departed
        this.activeSegments.some(segment => {
          if (![
            FLIGHT_STATUS.CANCEL.toString(),
            FLIGHT_STATUS.NOT_DEPARTED.toString()
          ].includes(segment.legStatus)) {
            this.activeSegments.shift()
            return false
          } else { return true }
        })

        if (this.activeSegments.length > 0) {

          // remove trailing segments with connection time greater than 12 hours
          let arrivalDateTime
          this.activeSegments.forEach((segment, i) => {
            if (i > 0) {
              if (Temporal.Instant.from(new Date(arrivalDateTime).toISOString()).until(Temporal.Instant.from(new Date(segment.departureDateTime).toISOString()), { smallestUnit: "minutes" }).minutes > 720) {
                this.activeSegments.splice(i)
              }
            }
            arrivalDateTime = segment.arrivalDateTime
          })

          // set the departure and arrival segments
          this.departureSegment = this.activeSegments[0]
          this.arrivalSegment = this.activeSegments[this.activeSegments.length - 1]

          // flight change
          if (this.activeSegments.every(segment => segment.legStatus !== FLIGHT_STATUS.CANCEL.toString())) {

            // check to ensure the departure segment has not departed and is no more than 24 hours from now
            if (
              this.departureSegment.legStatus !== FLIGHT_STATUS.NOT_DEPARTED.toString()
              || Temporal.Now.instant().add({ hours: 24 }).epochMilliseconds < this.departureSegment.departureDateTimeLong
            ) {
              this.activeSegments = []
            }

          }

          // flight rebook
          if (segments.some(segment => segment.legStatus === FLIGHT_STATUS.CANCEL.toString())) {

            // check to ensure the no more than 24 hours prior to now
            if (Temporal.Now.instant().subtract({ hours: 24 }).epochMilliseconds > this.departureSegment.departureDateTimeLong) {
              this.activeSegments = []
            }

          }

        }

      }

      const canRebookFlight = (
        this.config?.ENABLE_REBOOKING === true
        && this.sessionService.locationData['gate-number'] == GATE_CONSTANTS.GEN
        && this.activeSegments.length > 0
        //&& this.activeSegments.length < 3
        && this.departureSegment?.departureAirport?.iataCode != this.arrivalSegment?.arrivalAirport?.iataCode
        && this.activeSegments.some(segment => segment.legStatus == FLIGHT_STATUS.CANCEL.toString())
        && data.activateTravelPlan.travelPlan.canChangeFlight
      ) || false

      const canChangeFlight = (
        this.config?.ENABLE_REBOOKING === true
        && this.sessionService.locationData['gate-number'] == GATE_CONSTANTS.GEN
        && this.activeSegments.length > 0
        && this.departureSegment?.departureAirport?.iataCode != this.arrivalSegment?.arrivalAirport?.iataCode
        && this.activeSegments.every(segment => segment.legStatus !== FLIGHT_STATUS.CANCEL.toString())
        && data.activateTravelPlan.travelPlan.canChangeFlight
        && !canRebookFlight
      ) || false

      return {
        success: true,
        data: {
          confirmationID: data.activateTravelPlan.travelPlan.confirmationID,
          travelers: travelers,
          segments: segments,
          travelReady: travelReady,
          score: score,
          keyAttributes: keyAttributes,
          docCheckRegion: docCheckRegion,
          globalirrops: globalirrops,
          isIrrop: isIrrop,
          isInternational: isInternational,
          multiplePnr: false,
          canRebookFlight: canRebookFlight,
          canChangeFlight: canChangeFlight,
          allTravelers: data.activateTravelPlan.travelPlan.travelers,
          data: data
        }
      }

    } else {

      return {
        success: false,
        code: ((data && data.error && data.error == 'invalid_token') ? 403 : 200),
        data: data
      };

    }
  }

  /**
   * 
   * @desc user Date time as per offset from API
   * @requires departureSegment
   * @returns Temporal.ZonedDateTimeISO 
   */

  getUserZonedDateTime(departureSegment = this.departureSegment): Temporal.ZonedDateTime {
    let offSetString = Temporal.TimeZone.from(IATA_TIMEZONE[departureSegment.departureAirport.iataCode]).getOffsetStringFor(new Date().toISOString());
    let departDateTime = departureSegment.departureDateTime;
    let userDate = (departDateTime.includes('T')) ? departDateTime : new Date(`${departDateTime} ${offSetString}`).toISOString(); // different formats in property, handling the non ISO format using offSet string
    this.logger.debug("Flight Service .getUserDate", this);
    return Temporal.Instant.from(userDate).toZonedDateTimeISO(IATA_TIMEZONE[departureSegment.departureAirport.iataCode]);
  }

  parseFlightOptions(flightOptions: FlightOptionsResponse) {

    this.logger.debug('FlightService.parseFlightOptions()', this)

    flightOptions.trips?.map((trip) => {
      trip.flights.map((flight) => { // NOSONAR
        flight.available = this.isAvailableFlightOption(flight)

        flight.connectTimeMinutes = this.getTravelDuration(flight.departDateTime, flight.destinationDateTime)
        flight.connections.map((connection) => { // NOSONAR
          flight.available = flight.available && this.isAvailableFlightOption(connection)
          connection.connectTimeMinutes = this.getTravelTime(connection.connectTimeMinutes)
        })
        this.setFlightSegmentStatus(flight, 'flightStatuses')
      })
    });

    return flightOptions;
  }

  /*
    *  getKeyAttributes - get the key attributes
    *
    *  @param data - response object data
    */
  getKeyAttributes(data) {
    let key_attributes = '';
    if (data.activateTravelPlan.keyAttributes) {
      data.activateTravelPlan.keyAttributes.some(attribute => {
        key_attributes += `${attribute.ruleCode},`;
      })
      key_attributes = key_attributes.slice(0, -1);
    }
    return key_attributes;
  }

  /*
  *  getTraveler - get the current traveler, need the firstname
  *
  *  @param data - response object data
  */
  getTravelers(LastName, data) {
    let travelers = [];
    if (data.activateTravelPlan.travelPlan.travelers) {
      data.activateTravelPlan.travelPlan.travelers.map(traveler => {

        /*--------- commenting out as part of UAVA-766 -------------*/

        /* contactInfo = null;
  
        let phoneNumber = null;
  
        if(traveler.contactInfo){
          phoneNumber = traveler.contactInfo.phoneNumber;
        }
        else if(data.activateTravelPlan.travelPlan.phoneNumber){
          phoneNumber = data.activateTravelPlan.travelPlan.phoneNumber[0].number;
        }
  
        if(phoneNumber){
          phoneNumber = phoneNumber.replace(/\D/g, "");
          contactInfo = {
            code: phoneNumber.slice(0, phoneNumber.length > 10 ? phoneNumber.length - 10: 0),
            number: phoneNumber.slice(-10)
          }
        }*/

        if (String(traveler.surname).toUpperCase() == String(LastName).toUpperCase()) {
          travelers.push({
            givenName: traveler.givenName,
            surname: traveler.surname,
            firstNameNumber: traveler.firstNameNumber,
            surnameNumber: traveler.surnameNumber,
            mpNumber: traveler.mpNumber,
            mpType: traveler.mpType?.split(' ')[0] || '',
            //contactInfo: contactInfo,
            isCheckedIn: traveler.isCheckedIn,
            seats: traveler.seats,
            bagStatus: traveler.bagStatus,
            bagCount: traveler.bagCount
          })
        }
      });
      travelers = travelers.length ? travelers : data.activateTravelPlan.travelPlan.travelers;
    }
    return travelers.length ? travelers : [{
      "givenName": null,
      "surname": null,
      "firstNameNumber": null,
      "surnameNumber": null,
      //"contactInfo": null
      "isCheckedIn": null,
      "seats": null
    }];
  }

  /*
  *  getSegment - get the current segment
  *
  *  @param data - response object data
  */
  getSegments(timestamp, stationId, data) {
    let segments = [];
    if (data && data.activateTravelPlan
      && data.activateTravelPlan.travelPlan
      && data.activateTravelPlan.travelPlan.flightSegments
      && data.activateTravelPlan.travelPlan.flightSegments.length > 0) {
      if (timestamp) {
        let segmentsArr = data.activateTravelPlan.travelPlan.flightSegments;
        this.sortFlightSegments(segmentsArr);
        segmentsArr = segmentsArr.map(segment => {
          segment['activeFlightSegment'] = false;
          return this.setFlightSegmentStatus(segment);
        });
        let activeFlightSegmentIndex = this.findActiveSegment(segmentsArr, timestamp, stationId);
        segmentsArr[activeFlightSegmentIndex]['activeFlightSegment'] = true;
        data.activateTravelPlan.travelPlan.flightSegments = segmentsArr;
      }
      data.activateTravelPlan.travelPlan.flightSegments.forEach((segment, i) => {
        segments.push({
          activeFlightSegment: segment.activeFlightSegment,
          segmentNumber: segment.segmentNumber,
          flightNumber: segment.flightNumber,
          flightStatus: segment.flightStatus,
          legStatus: segment.legStatus,
          carrierCode: segment.carrierCode,
          carrierName: segment.carrierName,
          departureAirport: segment.departureAirport,
          localSchDeptDtm: segment.localSchDeptDtm,
          departureDateTime: segment.localEstDeptDtm ? segment.localEstDeptDtm : (segment.localSchDeptDtm ? segment.localSchDeptDtm : segment.departureDateTime),
          departureDateTimeLong: segment.gmtEstDeptDtmLong ? segment.gmtEstDeptDtmLong : (segment.gmtSchDeptDtmLong ? segment.gmtSchDeptDtmLong : segment.brdBgnDtmLong),
          departureTerminal: segment.departureTerminal || 'Terminal --',
          departureGate: segment.departureGate || '--',
          arrivalAirport: segment.arrivalAirport,
          arrivalDateTime: segment.localEstArrDtm ? segment.localEstArrDtm : (segment.localSchArrDtm ? segment.localSchArrDtm : segment.arrivalDateTime),
          arrivalTerminal: segment.arrivalTerminal || 'Terminal --',
          arrivalGate: segment.arrivalGate || '--',
          boardDateTime: segment.brdBgnDtm,
          boardDateTimeLong: segment.brdBgnDtmLong,
          travelTime: segment.travelTime,
          baggageCarouselId: segment.baggageCarouselId || '--',
          effectiveHub: segment.effectiveHub || '',
          isIrrop: segment.isIrrop || false,
        })
      })
    }
    return segments;
  }

  /*
  *  getStatus - get the current flight status
  *
  *  @param segment - response object data
  */
  getStatus(fltStatus) {
    if (fltStatus) {
      const status = fltStatus.find(a => a.statusType == "LegStatus");
      if (status && status.description) {
        let legStatus = status.description.toUpperCase();
        if (legStatus.substr(0, 6) == 'CANCEL') {
          legStatus = legStatus.substr(0, 6)
        }
        return legStatus.replaceAll(' ', '_');
      }
    }
    return 'UNAVAILABLE'
  }

  setStatus(fltStatus, statusType) {
    if (fltStatus) {
      const status = fltStatus.find(a => a.statusType == statusType);
      if (status && status.description) {
        return {
          description: status.description,
          className: status.description[0]
        }
      }
    }
    return {
      description: 'UNAVAILABLE',
      className: 'unavailable'
    }
  }

  getArrivalTime(segment) {
    let arrivalDateTime = segment.gmtEstArrDtm || segment.gmtSchArrDtm || segment.arrivalDateTime;
    return Temporal.Instant.from(new Date(arrivalDateTime).toISOString()).epochMilliseconds;
  }

  sortFlightSegments(segments) {
    segments.sort((a, b) => {
      return this.getArrivalTime(a) - this.getArrivalTime(b);
    });
    return segments;
  }

  setFlightSegmentStatus(segment, key = 'fltStatus') {
    let legStatus = this.getStatus(segment[key]);
    let flightStatus = null;
    if (['NOT_DEPARTED', 'RETURNED_TO_GATE', 'DEPARTED_GATE', 'RETURNED_TO_AIRPORT'].includes(legStatus)) {
      flightStatus = this.setStatus(segment[key], 'DepartureStatus');
    }
    else if (legStatus === 'CANCEL') {
      flightStatus = this.setStatus(segment[key], 'LegStatus');
    }
    else {
      flightStatus = this.setStatus(segment[key], 'ArrivalStatus');
    }
    segment.legStatus = legStatus;
    segment.flightStatus = flightStatus;
    return segment;
  }

  findActiveSegment(segments, timestamp, stationId) {
    let activeFlightSegment = -1;
    segments.some((segment, index) => {
      let status = segment.legStatus;
      let arrivalTime = this.getArrivalTime(segment);

      if (status.includes('CANCEL')) {
        activeFlightSegment = index;
        return true;  // If the segment is cancelled, need not check the other segments
      }
      if (activeFlightSegment != -1) {
        return false; // If there is an active segment already, only check for cancelled segments
      }
      if (['LANDED', 'ARRIVED_AT_GATE'].includes(status) && (index === segments.length - 1)) {
        activeFlightSegment = index;
        return true; // Need not check the next condition if it is the last segment
      }
      if (['NOT_DEPARTED', 'IN_FLIGHT', 'RETURNED_TO_GATE', 'DEPARTED_GATE', 'RETURNED_TO_AIRPORT'].includes(status)
        && (timestamp < arrivalTime || (stationId && stationId == segment.departureAirport.iataCode))) {
        activeFlightSegment = index;
        return false; // still need to check for segments with cancelled status
      }
      return false
    });
    return activeFlightSegment == -1 ? 0 : activeFlightSegment;
  }

  /**
   * 
   * @param bagDetails 
   * @returns delayed bags if present 
   */
  getDelayedBag(bagDetails: baggageResponse[] = this.activeTraveler.bagStatus) {
    let delayedBag = null;
    bagDetails.some(bag => { if (bag.status.indexOf(BAG_STATUS.DELAYED) >= 0) { delayedBag = bag.bagTagNumber }; })
    return delayedBag;
  }

  /**
   * 
   * @param segments 
   * @returns 
   */
  checkisIrrop(segments) {
    let isIrrop = false;
    segments.forEach((segment) => { segment.isIrrop && (isIrrop = true); })
    return isIrrop;
  }

  /**
   * 
   * @param selectedFlight 
   */
  async updateFlight(selectedFlight: FlightOption) {
    this.logger.debug("FlightService.updateFlight", this)
    const insertSegments: InsertFlightSegment[] = [];
    const deleteSegments: DeleteFlightSegment[] = [];
    insertSegments.push({
      dest: selectedFlight.destination,
      bookingClasses: this.getBookingClasses(selectedFlight),
      orig: selectedFlight.origin,
      departureDateTime: selectedFlight.departDateTime,
      flightNumber: selectedFlight.flightNumber,
      operatingCarrierCode: selectedFlight.operatingCarrierCode,
      travelerCount: this.flightDetails.allTravelers.length.toString(),
    })
    selectedFlight.connections.forEach(connection => {
      insertSegments.push({
        dest: connection.destination,
        bookingClasses: this.getBookingClasses(connection),
        orig: connection.origin,
        departureDateTime: connection.departDateTime,
        flightNumber: connection.flightNumber,
        operatingCarrierCode: connection.operatingCarrierCode,
        travelerCount: this.flightDetails.allTravelers.length.toString(),
      })
    })
    this.activeSegments.forEach(segment => {
      deleteSegments.push({
        segmentNumber: segment.segmentNumber.toString()
      })
    })
    return await this.changeFlightSegments({
      pnr: this.activeSegment.confirmationID,
      lastName: this.activeTraveler.surname,
      insertSegments: insertSegments,
      deleteSegments: deleteSegments
    })
  }

  /**
   * @desc Needed to compare if we are getting old data in PNR search
   * @returns flight service active segents flight numbers
   */
  getSegmentsFlightNumbers(segments = this.activeSegments): Array<number> {
    return segments?.map((segment) => segment?.flightNumber);
  }

  /**
   * 
   * @param depart 
   * @param arrival 
   * @returns 
   */
  getTravelDuration(depart, arrival) {
    this.logger.debug('FlightService.getTravelDuration', this);
    arrival = Temporal.Instant.from(new Date(arrival).toISOString()).epochMilliseconds;
    depart = Temporal.Instant.from(new Date(depart).toISOString()).epochMilliseconds;
    let differenceMinutes = ((arrival - depart) / 6e4);
    let hours = Math.floor(differenceMinutes / 60);
    let minutes = (differenceMinutes % 60);
    return `${hours}H ${minutes}M`;
  }

  /**
   * 
   * @param selectedFlight 
   * @returns 
   */
  getBookingClasses(selectedFlight: FlightOption) {
    this.logger.debug("FlightService.getBookingClass", this)
    let bookingClasses: InsertFlightSegmentBookingClasses[] = [];
    let allTravelers = Number(this.flightDetails.allTravelers.length)
    selectedFlight.bookingClassAvailability.split("|").forEach(bc => {
      const code = String(bc.match(/[a-zA-Z]+/g))
      const availableSeats = Number(bc.match(/\d+/g))
      if (code && availableSeats > 0 && allTravelers > 0) {
        allTravelers -= availableSeats
        bookingClasses.push({ code: code })
      }
    })
    return bookingClasses
  }

  /**
   * 
   * @param flight 
   */
  isAvailableFlightOption(flight: FlightOption) {
    this.logger.debug("FlightService.isAvailableFlightOption", this)
    let available = true
    let availableSeats = 0
    let departDateTime = new Date(flight.departDateTime);
    let userOffset = departDateTime.getTimezoneOffset() * 60000;
    let departOffset = Number(flight.departureTimeZone) * 36000;
    let departDateTimeEpochWithOffset = departDateTime.getTime() - userOffset;
    let departDateTimeEpochWithoutOffset = departDateTime.getTime() - userOffset - departOffset;

    // filter flights with the same flight number on the same departure date
    let searchDate = Temporal.PlainDate.from(this.flightOptionsCurrentDate.toString().split('T')[0])
    let departDate = Temporal.PlainDate.from(Temporal.Instant.fromEpochMilliseconds(departDateTimeEpochWithOffset).toString().split('T')[0])
    if (searchDate.dayOfYear == departDate.dayOfYear) {
      available = available && flight.flightNumber !== this.departureSegment?.flightNumber.toString()
    }
    this.logger.debug("FlightService.isAvailableFlightOption.flightNumber", available)

    // filter seats
    flight.bookingClassAvailability.split("|").forEach(bc => { availableSeats += Number(bc.match(/\d+/g)) })
    available = available && availableSeats >= Number(this.flightDetails.allTravelers.length)
    this.logger.debug("FlightService.isAvailableFlightOption.seats", available)

    // filter flights before the current time
    let now = Temporal.Now.instant()
    let then = Temporal.Instant.fromEpochMilliseconds(departDateTimeEpochWithoutOffset)
    let diff = now.until(then)
    available = available && diff.sign !== -1
    this.logger.debug("FlightService.isAvailableFlightOption.afterCurrentTime", available)

    return available

  }
}

const maxCalloutRetry = 2
